import { ActionIcon, Badge, Button, Card, FileButton, Flex, Grid, Group, Image, Modal, MultiSelect, NumberInput, SimpleGrid, Stack, Tabs, Text, TextInput, Textarea, Tooltip } from "@mantine/core"
import { DateInput } from "@mantine/dates"
import { useDisclosure, useMediaQuery } from "@mantine/hooks"
import { DataTable, DataTableSortStatus } from "mantine-datatable"
import { IconCalculator, IconCheck, IconDeviceMobile, IconEye, IconPlus, IconRobot, IconSearch, IconUpload, IconX } from "@tabler/icons-react"
import { useEffect, useMemo, useReducer, useState } from "react"
import _ from "lodash"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { PAGE_SIZES, ROLES } from "@/shared"
import { useGetViaticos, useCreateViatico, useUpdateViatico, useApproveViatico, usePayViatico, useAnalyzeViatico, useAnalyzeViaticoYape, useSubmitViaticoAccounting } from "../hooks/viatico-queries"
import { showNotification, updateNotification } from "@mantine/notifications"
import { fileToAttachment } from "@/shared"
import { useDynamicHeight } from "@/shared/hooks/responsive.queries"
import { headerStore } from "@shared/state/header.state.ts"
import { VIATICO_SECTIONS, VIATICO_SECTION_LABELS, Viatico, ViaticoItem, ViaticoSection, ViaticoSectionAccounting } from "../domain/viatico.interface"

type ViaticoMutation = ReturnType<typeof useAnalyzeViatico> | ReturnType<typeof useAnalyzeViaticoYape>

const getId = (v: Viatico) => v.id || (v as any)._id

const STATUS_COLOR: Record<string, string> = { pendiente: "yellow", aprobado: "green", pagado: "blue", contabilizado: "grape" }

const EMPTY_SECTION_ACCOUNTING: ViaticoSectionAccounting = { costCenter: "", accountingEntry: "", observations: "" }

function buildSectionPatch(section: ViaticoSection, items: ViaticoItem[]): Partial<Viatico> {
  switch (section) {
    case "movilizacion":
      return { movilizacion: items }
    case "alimentacion":
      return { alimentacion: items }
    case "hospedaje":
      return { hospedaje: items }
    case "varios":
      return { varios: items }
  }
}

interface SearchState {
  description: string
}

interface State {
  search: SearchState
  sortStatus: DataTableSortStatus<Viatico>
  page: number
  pageSize: number
}

type Action = { type: "SET_SEARCH"; payload: Partial<SearchState> } | { type: "SET_SORT_STATUS"; payload: DataTableSortStatus<Viatico> } | { type: "SET_PAGE"; payload: number } | { type: "SET_PAGE_SIZE"; payload: number }

const initialState: State = {
  search: { description: "" },
  sortStatus: { columnAccessor: "createdAt", direction: "desc" },
  page: 1,
  pageSize: PAGE_SIZES[1]
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_SEARCH":
      return { ...state, search: { ...state.search, ...action.payload }, page: 1 }
    case "SET_SORT_STATUS":
      return { ...state, sortStatus: action.payload }
    case "SET_PAGE":
      return { ...state, page: action.payload }
    case "SET_PAGE_SIZE":
      return { ...state, pageSize: action.payload, page: 1 }
    default:
      return state
  }
}

export default function ViaticoPage() {
  const { user, hasRole } = useAuth()
  const isMobile = useMediaQuery("(max-width: 36em)")
  const { tableHeight } = useDynamicHeight()
  const { data: viaticos, isLoading: fetching, refetch } = useGetViaticos()
  const [opened, { open, close }] = useDisclosure(false)

  const [description, setDescription] = useState("")
  const [workerName, setWorkerName] = useState("")
  const [workerDocument, setWorkerDocument] = useState("")
  const [provinces, setProvinces] = useState<string[]>([])
  const [departureDate, setDepartureDate] = useState<string | null>(null)
  const [returnDate, setReturnDate] = useState<string | null>(null)
  const [period, setPeriod] = useState("")
  const [attendees, setAttendees] = useState("")
  const [requestedAmount, setRequestedAmount] = useState<number>(0)
  const [depositDate, setDepositDate] = useState<string | null>(null)

  const createMutation = useCreateViatico()
  const updateMutation = useUpdateViatico()
  const approveMutation = useApproveViatico()
  const payMutation = usePayViatico()
  const analyzeMutation = useAnalyzeViatico()
  const analyzeYapeMutation = useAnalyzeViaticoYape()
  const submitAccountingMutation = useSubmitViaticoAccounting()

  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    headerStore.setTitle("Rendición de Gastos y Viáticos")
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [refetch])

  useEffect(() => {
    if (opened && user) {
      setWorkerName((prev) => prev || [user.firstName, user.lastName].filter(Boolean).join(" "))
    }
  }, [opened, user])

  const handleAnalyzeFile = async (file: File | null, viaticoId: string | undefined, section: ViaticoSection, mutation: ViaticoMutation, analyzingMessage: string) => {
    if (!file || !viaticoId) return

    try {
      showNotification({ title: "Subida", message: "Procesando imagen...", color: "blue" })
      const attachment = await fileToAttachment(file)

      showNotification({
        title: "IA",
        message: analyzingMessage,
        color: "violet",
        icon: <IconRobot size={18} />,
        loading: true,
        autoClose: false,
        id: "analyze-notification"
      })

      await mutation.mutateAsync({ id: viaticoId, imageUrl: attachment.contentBytes, section })

      updateNotification({
        id: "analyze-notification",
        title: "Éxito",
        message: "Comprobante analizado correctamente",
        color: "green",
        icon: <IconCheck size={18} />,
        loading: false,
        autoClose: true
      })
    } catch (error) {
      updateNotification({
        id: "analyze-notification",
        title: "Error",
        message: "No se pudo analizar la imagen",
        color: "red",
        loading: false,
        autoClose: true
      })
    }
  }

  const resetForm = () => {
    setDescription("")
    setWorkerName("")
    setWorkerDocument("")
    setProvinces([])
    setDepartureDate(null)
    setReturnDate(null)
    setPeriod("")
    setAttendees("")
    setRequestedAmount(0)
    setDepositDate(null)
  }

  const handleCreate = async () => {
    if (!description || provinces.length === 0 || !workerName) {
      showNotification({
        title: "Faltan campos requeridos",
        message: "Completa Nombres y Apellidos, Motivo del viaje y Ruta de viaje / Provincias antes de enviar",
        color: "red"
      })
      return
    }
    await createMutation.mutateAsync({
      description,
      workerName,
      workerDocument: workerDocument || undefined,
      provinces,
      departureDate: departureDate || undefined,
      returnDate: returnDate || undefined,
      period: period || undefined,
      attendees: attendees || undefined,
      requestedAmount,
      depositDate: depositDate || undefined,
      createdBy: user?.username || "sistema",
      currency: "PEN"
    })
    close()
    resetForm()
  }

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<string | null>("movilizacion")
  const [viewOpened, { open: openView, close: closeView }] = useDisclosure(false)
  const [accountingDraft, setAccountingDraft] = useState<Record<ViaticoSection, ViaticoSectionAccounting>>({
    movilizacion: { ...EMPTY_SECTION_ACCOUNTING },
    alimentacion: { ...EMPTY_SECTION_ACCOUNTING },
    hospedaje: { ...EMPTY_SECTION_ACCOUNTING },
    varios: { ...EMPTY_SECTION_ACCOUNTING }
  })

  const selectedViatico = Array.isArray(viaticos) ? viaticos.find((v) => getId(v) === selectedId) : undefined

  useEffect(() => {
    if (!selectedViatico) return
    setAccountingDraft({
      movilizacion: { ...EMPTY_SECTION_ACCOUNTING, ...selectedViatico.accounting?.movilizacion },
      alimentacion: { ...EMPTY_SECTION_ACCOUNTING, ...selectedViatico.accounting?.alimentacion },
      hospedaje: { ...EMPTY_SECTION_ACCOUNTING, ...selectedViatico.accounting?.hospedaje },
      varios: { ...EMPTY_SECTION_ACCOUNTING, ...selectedViatico.accounting?.varios }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, viewOpened])

  const filteredRecords = useMemo(() => {
    if (!viaticos) return []
    let result = [...viaticos]

    if (state.search.description) result = result.filter((v) => v.description?.toLowerCase().includes(state.search.description.toLowerCase()))

    result = _.orderBy(result, [state.sortStatus.columnAccessor], [state.sortStatus.direction])

    return result
  }, [viaticos, state.search, state.sortStatus])

  const pagedRecords = useMemo(() => {
    const from = (state.page - 1) * state.pageSize
    const to = from + state.pageSize
    return filteredRecords.slice(from, to)
  }, [filteredRecords, state.page, state.pageSize])

  const canDoAccounting = hasRole(ROLES.ADMIN) || hasRole(ROLES.RENDICION_CONTABLE)

  const handleUpdateDetail = (section: ViaticoSection, index: number, detail: string) => {
    if (!selectedViatico) return
    const items = [...selectedViatico[section]]
    if ((items[index].detail || "") === detail) return
    items[index] = { ...items[index], detail }
    updateMutation.mutate({ id: getId(selectedViatico), data: buildSectionPatch(section, items) })
  }

  const handleSubmitAccounting = () => {
    if (!selectedViatico) return
    submitAccountingMutation.mutate({ id: getId(selectedViatico), accounting: accountingDraft })
  }

  if (!hasRole(ROLES.ADMIN) && hasRole(ROLES.PROVIDER)) return null

  const canAccountThisRecord = (record: Viatico) => canDoAccounting && (record.status === "aprobado" || record.status === "pagado" || record.status === "contabilizado")

  const renderSection = (section: ViaticoSection) => {
    if (!selectedViatico) return null
    const items = selectedViatico[section]
    const viaticoId = getId(selectedViatico)

    return (
      <SimpleGrid cols={{ base: 1, sm: 2 }} mt="sm">
        {items.map((item, idx) => (
          <Card key={idx} withBorder padding="sm">
            <Card.Section>{item.imageUrl && <Image src={item.imageUrl} height={120} alt="Comprobante" />}</Card.Section>
            <Stack gap="xs" mt="sm">
              <Text fw={500}>{item.supplier}</Text>
              {item.ruc && <Text size="xs">RUC: {item.ruc}</Text>}
              <Group justify="space-between">
                <Text size="xs">
                  {item.documentType} {item.documentNumber}
                </Text>
                <Text size="sm" fw={700} c={item.currency && item.currency !== selectedViatico.currency ? "red" : undefined}>
                  {item.currency ?? selectedViatico.currency} {item.amount.toFixed(2)}
                </Text>
              </Group>
              {(item.taxableAmount !== undefined || item.taxAmount !== undefined) && (
                <Group gap="xs">
                  <Text size="xs" c="dimmed">
                    Gravada: {item.taxableAmount?.toFixed(2)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    IGV: {item.taxAmount?.toFixed(2)}
                  </Text>
                </Group>
              )}
              <TextInput size="xs" label="Detalle del gasto / motivo" placeholder="Ej: Almuerzo con cliente" defaultValue={item.detail} onBlur={(e) => handleUpdateDetail(section, idx, e.currentTarget.value)} />
            </Stack>
          </Card>
        ))}

        <Card withBorder padding="sm" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 120 }}>
          <Stack align="center" gap="xs">
            <FileButton onChange={(file) => handleAnalyzeFile(file, viaticoId, section, analyzeMutation, "La IA está analizando el comprobante...")} accept="image/png,image/jpeg">
              {(props) => (
                <Button {...props} variant="light" leftSection={<IconUpload size={18} />} color="violet">
                  Rendir con IA (Factura/Boleta)
                </Button>
              )}
            </FileButton>
            <FileButton onChange={(file) => handleAnalyzeFile(file, viaticoId, section, analyzeYapeMutation, "La IA está analizando el comprobante de Yape/Plin...")} accept="image/png,image/jpeg">
              {(props) => (
                <Button {...props} variant="light" leftSection={<IconDeviceMobile size={18} />} color="grape">
                  Rendir con IA (Yape/Plin)
                </Button>
              )}
            </FileButton>
            <Text size="xs" c="dimmed">
              La IA llenará los datos automáticamente
            </Text>
          </Stack>
        </Card>
      </SimpleGrid>
    )
  }

  return (
    <Stack gap="md">
      <Flex justify="flex-end">
        <Button leftSection={<IconPlus size={18} />} onClick={open}>
          Nueva rendición
        </Button>
      </Flex>

      <DataTable
        withTableBorder
        borderRadius="md"
        striped
        highlightOnHover
        fetching={fetching}
        records={pagedRecords}
        totalRecords={filteredRecords.length}
        recordsPerPage={state.pageSize}
        page={state.page}
        onPageChange={(p) => dispatch({ type: "SET_PAGE", payload: p })}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={(s) => dispatch({ type: "SET_PAGE_SIZE", payload: s })}
        recordsPerPageLabel="Cantidad por página"
        sortStatus={state.sortStatus}
        onSortStatusChange={(s) => dispatch({ type: "SET_SORT_STATUS", payload: s })}
        idAccessor={(record: Viatico) => getId(record)}
        noRecordsText="No hay rendiciones para mostrar"
        style={{ height: tableHeight }}
        columns={[
          {
            accessor: "description",
            title: "Motivo",
            sortable: true,
            filtering: state.search.description !== "",
            filter: (
              <TextInput
                placeholder="Buscar motivo"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.description && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => dispatch({ type: "SET_SEARCH", payload: { description: "" } })}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.description}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { description: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "workerName",
            title: "Trabajador"
          },
          {
            accessor: "provinces",
            title: "Provincias",
            render: (record) => record.provinces.join(", ")
          },
          {
            accessor: "requestedAmount",
            title: "Monto del Viático",
            sortable: true,
            render: (record) => `${record.currency} ${record.requestedAmount?.toFixed(2)}`
          },
          {
            accessor: "totalAmount",
            title: "Rendido",
            sortable: true,
            render: (record) => `${record.currency} ${record.totalAmount?.toFixed(2)}`
          },
          {
            accessor: "status",
            title: "Estado",
            sortable: true,
            render: (record) => <Badge color={STATUS_COLOR[record.status]}>{record.status.toUpperCase()}</Badge>
          },
          {
            accessor: "actions",
            title: "Acciones",
            textAlign: "right",
            render: (record) => (
              <Group gap={4} justify="right" wrap="nowrap">
                <Tooltip label="Ver detalle">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    onClick={() => {
                      setSelectedId(getId(record))
                      setActiveTab("movilizacion")
                      openView()
                    }}
                  >
                    <IconEye size={16} />
                  </ActionIcon>
                </Tooltip>
                {record.status === "pendiente" && (
                  <Tooltip label="Aprobar">
                    <ActionIcon size="sm" variant="subtle" color="green" onClick={() => approveMutation.mutate({ id: getId(record), approvedBy: user?.username || "" })}>
                      <IconCheck size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {record.status === "aprobado" && (
                  <Tooltip label="Marcar como pagado">
                    <ActionIcon size="sm" variant="subtle" color="blue" onClick={() => payMutation.mutate({ id: getId(record), paidBy: user?.username || "" })}>
                      <IconCheck size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {canAccountThisRecord(record) && (
                  <Tooltip label="Contabilizar">
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="grape"
                      onClick={() => {
                        setSelectedId(getId(record))
                        setActiveTab("contabilidad")
                        openView()
                      }}
                    >
                      <IconCalculator size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            )
          }
        ]}
      />

      <Modal opened={opened} onClose={close} title="Nueva Rendición de Gastos y Viáticos" size="lg" centered fullScreen={isMobile}>
        <Stack>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="Nombres y Apellidos" value={workerName} onChange={(e) => setWorkerName(e.currentTarget.value)} required />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="D.N.I. / C.E. / Pasaporte" value={workerDocument} onChange={(e) => setWorkerDocument(e.currentTarget.value)} />
            </Grid.Col>
          </Grid>
          <TextInput label="Motivo del viaje" placeholder="Ej: Visita técnica a sedes del norte" value={description} onChange={(e) => setDescription(e.currentTarget.value)} required />
          <MultiSelect label="Ruta de viaje / Provincias" placeholder="Selecciona las provincias" data={["Lima", "Trujillo", "Chiclayo", "Piura", "Arequipa", "Cusco", "Iquitos", "Huancayo"]} value={provinces} onChange={setProvinces} searchable required />
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <DateInput label="Fecha de Salida" valueFormat="DD/MM/YYYY" value={departureDate} onChange={setDepartureDate} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <DateInput label="Fecha de Llegada" valueFormat="DD/MM/YYYY" value={returnDate} onChange={setReturnDate} />
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="Periodo" placeholder="Ej: Julio 2026" value={period} onChange={(e) => setPeriod(e.currentTarget.value)} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="Personas asistentes" placeholder="Nombres separados por coma" value={attendees} onChange={(e) => setAttendees(e.currentTarget.value)} />
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <NumberInput label="Importe de Viáticos (PEN)" value={requestedAmount} onChange={(val) => setRequestedAmount(Number(val))} min={0} prefix="S/ " />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <DateInput label="Fecha de Depósito" valueFormat="DD/MM/YYYY" value={depositDate} onChange={setDepositDate} />
            </Grid.Col>
          </Grid>
          <Button onClick={handleCreate} loading={createMutation.isPending}>
            Enviar Rendición
          </Button>
        </Stack>
      </Modal>

      <Modal opened={viewOpened} onClose={closeView} title={`Rendición: ${selectedViatico?.description ?? ""}`} size="xl" centered fullScreen={isMobile}>
        {selectedViatico && (
          <Stack>
            <Group justify="space-between">
              <Stack gap={0}>
                <Text size="sm" c="dimmed">
                  {selectedViatico.workerName}
                  {selectedViatico.workerDocument && ` · ${selectedViatico.workerDocument}`}
                </Text>
                <Badge color={STATUS_COLOR[selectedViatico.status]}>{selectedViatico.status.toUpperCase()}</Badge>
              </Stack>
              <Stack gap={0} align="flex-end">
                <Text size="sm" c="dimmed">
                  Total Rendido:
                </Text>
                <Text fw={700} size="xl">
                  {selectedViatico.currency} {selectedViatico.totalAmount?.toFixed(2)}
                </Text>
              </Stack>
            </Group>

            <Tabs value={activeTab} onChange={setActiveTab}>
              <div style={{ overflowX: "auto" }}>
                <Tabs.List style={{ flexWrap: "nowrap" }}>
                  {VIATICO_SECTIONS.map((section) => (
                    <Tabs.Tab key={section} value={section} style={{ flexShrink: 0 }}>
                      {VIATICO_SECTION_LABELS[section]}
                    </Tabs.Tab>
                  ))}
                  {canAccountThisRecord(selectedViatico) && (
                    <Tabs.Tab value="contabilidad" style={{ flexShrink: 0 }}>
                      Contabilidad
                    </Tabs.Tab>
                  )}
                </Tabs.List>
              </div>

              {VIATICO_SECTIONS.map((section) => (
                <Tabs.Panel key={section} value={section}>
                  {renderSection(section)}
                </Tabs.Panel>
              ))}

              {canAccountThisRecord(selectedViatico) && (
                <Tabs.Panel value="contabilidad">
                  <Stack mt="sm">
                    {VIATICO_SECTIONS.map((section) => (
                      <Card key={section} withBorder padding="sm">
                        <Text fw={500} mb="xs">
                          {VIATICO_SECTION_LABELS[section]}
                        </Text>
                        <Grid>
                          <Grid.Col span={{ base: 12, sm: 4 }}>
                            <TextInput
                              label="Centro de Gastos"
                              value={accountingDraft[section].costCenter}
                              onChange={(e) => {
                                const value = e.currentTarget.value
                                setAccountingDraft((prev) => ({ ...prev, [section]: { ...prev[section], costCenter: value } }))
                              }}
                            />
                          </Grid.Col>
                          <Grid.Col span={{ base: 12, sm: 4 }}>
                            <TextInput
                              label="Registro"
                              value={accountingDraft[section].accountingEntry}
                              onChange={(e) => {
                                const value = e.currentTarget.value
                                setAccountingDraft((prev) => ({ ...prev, [section]: { ...prev[section], accountingEntry: value } }))
                              }}
                            />
                          </Grid.Col>
                          <Grid.Col span={{ base: 12, sm: 4 }}>
                            <Textarea
                              label="Observaciones"
                              autosize
                              minRows={1}
                              value={accountingDraft[section].observations}
                              onChange={(e) => {
                                const value = e.currentTarget.value
                                setAccountingDraft((prev) => ({ ...prev, [section]: { ...prev[section], observations: value } }))
                              }}
                            />
                          </Grid.Col>
                        </Grid>
                      </Card>
                    ))}
                    <Button onClick={handleSubmitAccounting} loading={submitAccountingMutation.isPending}>
                      Guardar Contabilidad
                    </Button>
                  </Stack>
                </Tabs.Panel>
              )}
            </Tabs>
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}
